import { isoDateTime, z } from "../zod.js";
import { Uuid } from "../identity/admin-schemas.js";
import { Problem } from "../common/problem.js";
import {
  SAVED_VIEW_NUL_ISSUE,
  SAVED_VIEW_SCREENS,
  SavedViewScreenKey,
  savedViewTextHasNoNul,
  type SavedViewFilterSpec,
  type SavedViewScreenKeyName,
} from "./screens.js";

// THE SAVED-VIEW CONTRACT (spec 16 §2.20, §3.12 and §5; D-243, D-305).
//
// A view's `config` is `{v: 1, filters, order, columns}` and all four fields
// are required, so a config has one spelling. The system rows 0070 inserts are
// written in full for that reason, and `SYSTEM_SAVED_VIEWS` parses through the
// same schema (D-305 ruling 11). `filters` is keyed by the screen's URL filter
// keys and typed by the registry in `./screens.ts`. `order` is the screen's own
// sort token, or null for the declared default. `columns` is an ordered list
// of at most 40 entries, and two entries naming one key are refused.
//
// A write is validated strictly. A POST goes through a union keyed on its
// `screen_key`. A PATCH names no screen, so its schema is the union of the
// screens' configs, and the service validates a PATCH `config` again against
// the stored row's `screen_key` once the row checks have passed (spec 16 §5).
// A read returns the config as stored, and the client re-validates it through
// the screen's own URL validator, because a filter a screen accepted when the
// view was saved may be one it no longer accepts (spec 16 §3.12 item 7).

// --- columns (spec 16 §3.12 item 2, design-system §3.3) ---------------------

export const SAVED_VIEW_MAX_COLUMNS = 40;
/** A column key. `_sel`, the selection column, does not match it, and the
 * table ignores any entry naming it anyway (design-system §3.3). */
export const SAVED_VIEW_COLUMN_KEY_GRAMMAR = /^[a-z][a-z0-9_]{0,63}$/u;
export const SAVED_VIEW_COLUMN_WIDTH_MIN = 64;
export const SAVED_VIEW_COLUMN_WIDTH_MAX = 640;

export const SavedViewColumn = z
  .object({
    key: z.string().regex(SAVED_VIEW_COLUMN_KEY_GRAMMAR),
    /** Absent means the column's default, so a column hidden by default can
     * be shown with `hidden: false`. */
    hidden: z.boolean().optional(),
    /** Whole pixels. Phones render no widths. */
    width: z
      .number()
      .int()
      .min(SAVED_VIEW_COLUMN_WIDTH_MIN)
      .max(SAVED_VIEW_COLUMN_WIDTH_MAX)
      .optional(),
  })
  .strict()
  .openapi("SavedViewColumn");

/** The ordered column list. Empty leaves every column at its default. A key
 * named twice is refused 400 `VALIDATION_FAILED` with the second entry's path,
 * because the table would keep the first in silence and the second would be a
 * setting that does nothing. */
export const SavedViewColumns = z
  .array(SavedViewColumn)
  .max(SAVED_VIEW_MAX_COLUMNS)
  .superRefine((columns, ctx) => {
    const seen = new Set<string>();
    columns.forEach((column, index) => {
      if (seen.has(column.key)) {
        ctx.addIssue({
          code: "custom",
          path: [index, "key"],
          message: `column key "${column.key}" is named twice`,
          params: { reason: "duplicate_column_key", key: column.key },
        });
      }
      seen.add(column.key);
    });
  });

// --- the 16 KB bound (spec 16 §2.20) ----------------------------------------

/** The database's CHECK: `octet_length(config::text) <= 16384`. */
export const SAVED_VIEW_CONFIG_MAX_BYTES = 16_384;

/**
 * The bytes `octet_length(config::text)` reports for this value once it is
 * stored as jsonb. Postgres prints jsonb with a space after every `:` and `,`,
 * so its text is longer than `JSON.stringify`'s, and measuring the shorter
 * form would let a config through this schema that the CHECK then refuses as a
 * 500. Strings escape as `JSON.stringify` escapes them, which is how Postgres
 * escapes them too, and key order does not change the length.
 */
export function savedViewConfigBytes(value: unknown): number {
  return new TextEncoder().encode(jsonbText(value)).length;
}

function jsonbText(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(jsonbText).join(", ")}]`;
  if (value !== null && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([, member]) => member !== undefined,
    );
    return `{${entries.map(([key, member]) => `${JSON.stringify(key)}: ${jsonbText(member)}`).join(", ")}}`;
  }
  return JSON.stringify(value);
}

// --- the per-screen configs --------------------------------------------------

type FiltersShape<F extends Readonly<Record<string, SavedViewFilterSpec>>> = {
  -readonly [K in keyof F]: z.ZodOptional<F[K]["schema"]>;
};

function filtersSchema<F extends Readonly<Record<string, SavedViewFilterSpec>>>(filters: F) {
  const shape: Record<string, z.ZodType> = {};
  for (const [key, spec] of Object.entries(filters)) shape[key] = spec.schema.optional();
  return z.object(shape as FiltersShape<F>).strict();
}

/**
 * The strict config schema for one screen's registry entry. Exported so the
 * contracts test can hold the 16 KB bound against a screen whose grammar
 * reaches it, since none of the six adopting screens' grammars can (the test
 * builds the largest config each one admits and measures it).
 */
export function savedViewConfigSchemaFor<
  F extends Readonly<Record<string, SavedViewFilterSpec>>,
  T extends Readonly<Record<string, string>>,
>(
  screen: { readonly filters: F; readonly order: { readonly schema: z.ZodEnum<T> } },
  name?: string,
) {
  const schema = z
    .object({
      v: z.literal(1),
      filters: filtersSchema(screen.filters),
      // An inline copy of the route's enum rather than a reference to it. A
      // nullable reference renders as `allOf` with a null type, and the
      // generated client reads that intersection as the enum alone, so the
      // web would be told `order` is never null.
      order: z.enum(screen.order.schema.enum).nullable(),
      columns: SavedViewColumns,
    })
    .strict()
    .superRefine((config, ctx) => {
      const bytes = savedViewConfigBytes(config);
      if (bytes > SAVED_VIEW_CONFIG_MAX_BYTES) {
        ctx.addIssue({
          code: "custom",
          message: `config is ${bytes} bytes as stored, above ${SAVED_VIEW_CONFIG_MAX_BYTES}`,
          params: { reason: "config_too_large", maximum: SAVED_VIEW_CONFIG_MAX_BYTES },
        });
      }
    });
  if (name === undefined) return schema;
  return schema.openapi(name, {
    description:
      "A saved view's config for one screen (spec 16 §3.12 item 2). All four fields are required. `order` is null for the screen's declared default. At most 16 384 bytes as stored.",
  });
}

export const SavedViewWorkOrdersConfig = savedViewConfigSchemaFor(
  SAVED_VIEW_SCREENS["production.work_orders"],
  "SavedViewWorkOrdersConfig",
);
export const SavedViewCustomersConfig = savedViewConfigSchemaFor(
  SAVED_VIEW_SCREENS["sales.customers"],
  "SavedViewCustomersConfig",
);
export const SavedViewQuotesConfig = savedViewConfigSchemaFor(
  SAVED_VIEW_SCREENS["sales.quotes"],
  "SavedViewQuotesConfig",
);
export const SavedViewOrdersConfig = savedViewConfigSchemaFor(
  SAVED_VIEW_SCREENS["sales.orders"],
  "SavedViewOrdersConfig",
);
/** P3-E2 PR E2-1 (D-314 ruling 6): the deal list's views. */
export const SavedViewOpportunitiesConfig = savedViewConfigSchemaFor(
  SAVED_VIEW_SCREENS["sales.opportunities"],
  "SavedViewOpportunitiesConfig",
);
/** P3-E2 PR E2-1 (D-314 ruling 6): the task list's views. */
export const SavedViewTasksConfig = savedViewConfigSchemaFor(
  SAVED_VIEW_SCREENS["sales.tasks"],
  "SavedViewTasksConfig",
);

export type SavedViewWorkOrdersConfigValue = z.infer<typeof SavedViewWorkOrdersConfig>;
export type SavedViewCustomersConfigValue = z.infer<typeof SavedViewCustomersConfig>;
export type SavedViewQuotesConfigValue = z.infer<typeof SavedViewQuotesConfig>;
export type SavedViewOrdersConfigValue = z.infer<typeof SavedViewOrdersConfig>;
export type SavedViewOpportunitiesConfigValue = z.infer<typeof SavedViewOpportunitiesConfig>;
export type SavedViewTasksConfigValue = z.infer<typeof SavedViewTasksConfig>;

/** Each screen's strict config schema, by key. The service validates a PATCH
 * `config` with the stored row's entry (spec 16 §5). */
export const SAVED_VIEW_CONFIG_SCHEMAS = {
  "production.work_orders": SavedViewWorkOrdersConfig,
  "sales.customers": SavedViewCustomersConfig,
  "sales.quotes": SavedViewQuotesConfig,
  "sales.orders": SavedViewOrdersConfig,
  "sales.opportunities": SavedViewOpportunitiesConfig,
  "sales.tasks": SavedViewTasksConfig,
} as const satisfies Record<SavedViewScreenKeyName, z.ZodType>;

/** The union of the screens' configs, the PATCH route's schema. It accepts a
 * config some screen's grammar accepts, and says nothing about which screen. */
export const SavedViewConfig = z
  .union([
    SavedViewWorkOrdersConfig,
    SavedViewCustomersConfig,
    SavedViewQuotesConfig,
    SavedViewOrdersConfig,
    SavedViewOpportunitiesConfig,
    SavedViewTasksConfig,
  ])
  .openapi("SavedViewConfig");

// --- names (spec 16 §2.20 and §3.12 item 3) ----------------------------------

export const SAVED_VIEW_NAME_MAX = 60;

/**
 * A view's name, 1 to 60 characters after trimming. Characters are counted as
 * code points, the unit the database's `char_length` counts, so a name the
 * CHECK accepts is never refused here for being written in a script whose
 * characters take two UTF-16 units. A NUL character is refused, because
 * Postgres text cannot hold one and the insert would end as a 500.
 */
export const SavedViewName = z
  .string()
  .trim()
  .refine(savedViewTextHasNoNul, SAVED_VIEW_NUL_ISSUE)
  .superRefine((name, ctx) => {
    // `.` with the `u` flag matches one code point, and `s` lets it match a
    // line break too.
    const length = name.match(/./gsu)?.length ?? 0;
    if (length < 1) {
      ctx.addIssue({
        code: "too_small",
        minimum: 1,
        origin: "string",
        inclusive: true,
      });
    } else if (length > SAVED_VIEW_NAME_MAX) {
      ctx.addIssue({
        code: "too_big",
        maximum: SAVED_VIEW_NAME_MAX,
        origin: "string",
        inclusive: true,
      });
    }
  })
  .openapi({
    minLength: 1,
    maxLength: SAVED_VIEW_NAME_MAX,
    description:
      "1 to 60 characters after trimming, with no NUL character (U+0000). A member's own names are unique per screen ignoring case, and shared names are unique per tenant and screen (409 `SAVED_VIEW_NAME_TAKEN`).",
  });

// --- requests ----------------------------------------------------------------

function createVariant<K extends SavedViewScreenKeyName, C extends z.ZodType>(key: K, config: C) {
  return z
    .object({
      screen_key: z.literal(key),
      name: SavedViewName,
      config,
      /** Private unless the author shares it. */
      shared: z.boolean().optional(),
    })
    .strict();
}

/** POST body. Validated strictly through a union keyed on `screen_key`, so an
 * unknown key and a config the named screen's grammar refuses both answer
 * 400 `VALIDATION_FAILED` at the pipe. */
export const SavedViewCreateRequest = z
  .discriminatedUnion("screen_key", [
    createVariant("production.work_orders", SavedViewWorkOrdersConfig),
    createVariant("sales.customers", SavedViewCustomersConfig),
    createVariant("sales.quotes", SavedViewQuotesConfig),
    createVariant("sales.orders", SavedViewOrdersConfig),
    createVariant("sales.opportunities", SavedViewOpportunitiesConfig),
    createVariant("sales.tasks", SavedViewTasksConfig),
  ])
  .openapi("SavedViewCreateRequest");

export type SavedViewCreateRequestBody = z.infer<typeof SavedViewCreateRequest>;

/** PATCH body. It names no screen, so `config` is checked here against every
 * screen's grammar and again by the service against the stored row's. */
export const SavedViewPatchRequest = z
  .object({
    name: SavedViewName.optional(),
    config: SavedViewConfig.optional(),
    shared: z.boolean().optional(),
  })
  .strict()
  .refine(
    (patch) => patch.name !== undefined || patch.config !== undefined || patch.shared !== undefined,
    { message: "at least one of name, config or shared is required" },
  )
  .openapi("SavedViewPatchRequest");

export type SavedViewPatchRequestBody = z.infer<typeof SavedViewPatchRequest>;

export const SavedViewListQuery = z.object({
  /** Required (D-142). An unknown key answers 400 `VALIDATION_FAILED`. */
  screen_key: SavedViewScreenKey,
});

// --- responses ---------------------------------------------------------------

/** The config as the row stores it. Typed loosely on purpose: a stored filter
 * may be one the screen no longer accepts, and the client drops it with a
 * notice rather than the read failing (spec 16 §3.12 item 7). */
export const SavedViewStoredConfig = z
  .object({
    v: z.number().int(),
    filters: z.record(z.string(), z.unknown()),
    order: z.string().nullable(),
    columns: z.array(
      z.object({
        key: z.string(),
        hidden: z.boolean().optional(),
        width: z.number().int().optional(),
      }),
    ),
  })
  .openapi("SavedViewStoredConfig", {
    description:
      "The config as stored. The client re-validates it through the screen's own URL validator, dropping a filter the screen no longer accepts with a notice and a column key the table no longer has in silence (spec 16 §3.12 item 7).",
  });

export const SavedView = z
  .object({
    id: Uuid,
    screen_key: SavedViewScreenKey,
    /** A member's view carries the name its author typed. A system view
     * carries an i18n key, and only a system name goes through `t()`. */
    name: z.string(),
    config: SavedViewStoredConfig,
    shared: z.boolean(),
    is_system: z.boolean(),
    /** The caller is the view's author. No system view has one. */
    mutable: z.boolean(),
    /** The view is not a system view, it is shared, the caller holds
     * `identity.manage_shared_views` through a group-scoped grant, and the
     * caller is not its author. */
    can_moderate: z.boolean(),
    created_at: isoDateTime(),
    updated_at: isoDateTime(),
  })
  .openapi("SavedView", {
    description:
      "One saved view as the caller may use it (spec 16 §5). No item names its author. `mutable` and `can_moderate` are computed by the server and read no session mode, and both are false on a system view for every reader, group_admin included.",
  });

export const SavedViewListResponse = z
  .object({ items: z.array(SavedView) })
  .openapi("SavedViewListResponse", {
    description:
      "The screen's system views, the caller's own views and the tenant's shared views. The caps bound the list (20 own and 20 shared per screen), so it has no cursor.",
  });

// --- problems (spec 16 §5) ---------------------------------------------------

/**
 * RFC 9457 problem codes this surface adds (D-022 UPPER_SNAKE machine keys),
 * in the shape of `FILE_PROBLEM_CODES`.
 *
 * The surface's 400 is the platform's `VALIDATION_FAILED` and its 404 the
 * platform's `NOT_FOUND`, so another member's private view and an unknown id
 * answer the same thing. Neither is listed, since neither is new.
 */
export const SAVED_VIEW_PROBLEM_CODES = [
  "SAVED_VIEW_NAME_TAKEN",
  "SAVED_VIEW_LIMIT_REACHED",
  "SAVED_VIEW_SYSTEM_IMMUTABLE",
  "SAVED_VIEW_NOT_OWNER",
] as const;

export type SavedViewProblemCode = (typeof SAVED_VIEW_PROBLEM_CODES)[number];

/** Which cap a 409 `SAVED_VIEW_LIMIT_REACHED` met. `member` is the caller's
 * own 20 views on the screen and `shared` the tenant's 20 shared views on the
 * screen. The member cap is checked first, so a POST that meets both answers
 * `member`. */
export const SAVED_VIEW_CAPS = ["member", "shared"] as const;
export type SavedViewCap = (typeof SAVED_VIEW_CAPS)[number];

/** Both caps hold 20 views per screen. */
export const SAVED_VIEW_CAP_LIMIT = 20;

/** The two extensions 409 `SAVED_VIEW_LIMIT_REACHED` carries, merged into the
 * problem document beside `code` as every RFC 9457 extension is. */
export const SavedViewLimitReachedExtensions = z
  .object({
    cap: z.enum(SAVED_VIEW_CAPS),
    limit: z.literal(SAVED_VIEW_CAP_LIMIT),
  })
  .strict();

export type SavedViewLimitReachedExtensionsValue = z.infer<typeof SavedViewLimitReachedExtensions>;

/** The 409 body of the POST and PATCH routes. `cap` and `limit` are present
 * on `SAVED_VIEW_LIMIT_REACHED` and on no other code. */
export const SavedViewConflictProblem = Problem.extend({
  cap: z.enum(SAVED_VIEW_CAPS).optional(),
  limit: z.literal(SAVED_VIEW_CAP_LIMIT).optional(),
}).openapi("SavedViewConflictProblem", {
  description:
    "A saved-view conflict. On `SAVED_VIEW_LIMIT_REACHED` it carries `cap` (`member` or `shared`) and `limit` (20), so the form can name the full cap (spec 16 §4).",
});

// --- audit (spec 16 §3.12 item 11, D-305 ruling 24) ---------------------------

/** The three audit actions. They sit in the identity namespace, so the
 * `/audit` screen's Identity area finds them and the existing `identity.`
 * payload rule covers them. None starts with `platform.`, which the
 * platform-layer event census would read as an event. */
export const SAVED_VIEW_AUDIT_ACTIONS = {
  created: "identity.saved_view_created",
  updated: "identity.saved_view_updated",
  deleted: "identity.saved_view_deleted",
} as const;

export const SAVED_VIEW_AUDIT_ENTITY_TYPE = "saved_view";
